<template>
  <Modal
    :open="showReminder"
    :title="t('responsible_gaming.session_reminder_title')"
    :close-button="false"
    size="sm"
    :closeable="false"
  >
    <div class="reminder-content">
      <div class="reminder-icon">&#9201;</div>
      <p class="reminder-message">
        {{ t('responsible_gaming.playing_for_prefix') }}<strong>{{ formattedSessionTime }}</strong>{{ t('responsible_gaming.playing_for_suffix') }}
      </p>

      <div v-if="sessionStats" class="session-stats">
        <div class="stat-row">
          <span class="stat-label">{{ t('responsible_gaming.total_wagered') }}</span>
          <span class="stat-value">${{ formatCents(sessionStats.total_wagered_cents) }}</span>
        </div>
        <div class="stat-row">
          <span class="stat-label">{{ t('responsible_gaming.total_won') }}</span>
          <span class="stat-value">${{ formatCents(sessionStats.total_won_cents) }}</span>
        </div>
        <div class="stat-row stat-row--highlight">
          <span class="stat-label">{{ netResultLabel }}</span>
          <span :class="['stat-value', netResultClass]">{{ formattedNetResult }}</span>
        </div>
      </div>

      <p class="reminder-sub">
        {{ t('responsible_gaming.continue_reminder') }}
      </p>

      <div class="resources-section">
        <p class="resources-title">{{ t('responsible_gaming.resources_title') }}</p>
        <div class="resources-links">
          <a href="https://www.gamcare.org.uk" target="_blank" rel="noopener noreferrer" class="resource-link">GamCare</a>
          <a href="https://www.begambleaware.org" target="_blank" rel="noopener noreferrer" class="resource-link">BeGambleAware</a>
          <a href="https://www.gamblersanonymous.org" target="_blank" rel="noopener noreferrer" class="resource-link">Gamblers Anonymous</a>
        </div>
        <router-link to="/player/responsible-gaming" class="resource-link resource-link--internal" @click="handleDismiss">
          {{ t('responsible_gaming.manage_limits') }}
        </router-link>
      </div>
    </div>

    <template #footer>
      <button class="btn btn-outline" @click="handleLogout">{{ t('nav.logout') }}</button>
      <button class="btn btn-primary" @click="handleDismiss">{{ t('responsible_gaming.continue_playing') }}</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import Modal from './Modal.vue'
import apiClient from '@/api/client'

const { t } = useI18n()

interface SessionStatsResponse {
  net_loss_cents: number
  total_wagered_cents: number
  total_won_cents: number
  session_duration_seconds: number
}

const props = withDefaults(
  defineProps<{
    reminderMinutes?: number
  }>(),
  {
    reminderMinutes: 30,
  }
)

const emit = defineEmits<{
  logout: []
}>()

const showReminder = ref(false)
const sessionStartTime = ref(Date.now())
const now = ref(Date.now())
const sessionStats = ref<SessionStatsResponse | null>(null)

let inactivityTimer: ReturnType<typeof setTimeout> | null = null
let clockInterval: ReturnType<typeof setInterval> | null = null

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const

const formattedSessionTime = computed(() => {
  const totalSeconds = Math.floor((now.value - sessionStartTime.value) / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }
  return `${seconds}s`
})

const netResultLabel = computed(() => {
  if (!sessionStats.value) return ''
  return sessionStats.value.net_loss_cents > 0 ? t('responsible_gaming.session_loss') : t('responsible_gaming.session_profit')
})

const netResultClass = computed(() => {
  if (!sessionStats.value) return ''
  return sessionStats.value.net_loss_cents > 0 ? 'stat-value--loss' : 'stat-value--profit'
})

const formattedNetResult = computed(() => {
  if (!sessionStats.value) return ''
  const cents = sessionStats.value.net_loss_cents
  if (cents > 0) {
    return `-$${formatCents(cents)}`
  }
  return `+$${formatCents(Math.abs(cents))}`
})

function formatCents(cents: number): string {
  return (Math.abs(cents) / 100).toFixed(2)
}

async function fetchSessionStats() {
  try {
    const response = await apiClient.get<SessionStatsResponse>('/api/session/stats')
    sessionStats.value = response.data
  } catch {
    // Silently fail — popup still works without stats
    sessionStats.value = null
  }
}

// Fetch session stats when popup opens
watch(showReminder, (visible) => {
  if (visible) {
    fetchSessionStats()
  }
})

function resetInactivityTimer() {
  if (showReminder.value) return
  if (props.reminderMinutes <= 0) return

  if (inactivityTimer !== null) {
    clearTimeout(inactivityTimer)
  }

  inactivityTimer = setTimeout(() => {
    showReminder.value = true
  }, props.reminderMinutes * 60 * 1000)
}

function handleDismiss() {
  showReminder.value = false
  sessionStats.value = null
  resetInactivityTimer()
}

function handleLogout() {
  showReminder.value = false
  emit('logout')
}

function onActivity() {
  resetInactivityTimer()
}

onMounted(() => {
  sessionStartTime.value = Date.now()

  // Update clock every second for session time display
  clockInterval = setInterval(() => {
    now.value = Date.now()
  }, 1000)

  if (props.reminderMinutes > 0) {
    // Listen for user activity
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { passive: true })
    }

    // Start the inactivity timer
    resetInactivityTimer()
  }
})

onUnmounted(() => {
  if (inactivityTimer !== null) {
    clearTimeout(inactivityTimer)
  }
  if (clockInterval !== null) {
    clearInterval(clockInterval)
  }

  for (const event of ACTIVITY_EVENTS) {
    document.removeEventListener(event, onActivity)
  }
})
</script>

<style scoped>
.reminder-content {
  text-align: center;
}

.reminder-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.reminder-message {
  font-size: 1rem;
  color: var(--text-primary, #fff);
  margin: 0 0 12px;
}

.reminder-sub {
  font-size: 0.875rem;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
  line-height: 1.5;
}

.session-stats {
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  padding: 12px 16px;
  margin: 16px 0;
  text-align: left;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.stat-row--highlight {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 4px;
  padding-top: 8px;
  font-weight: 600;
}

.stat-label {
  font-size: 0.8125rem;
  color: var(--text-secondary, #b1bad3);
}

.stat-value {
  font-size: 0.875rem;
  color: var(--text-primary, #fff);
}

.stat-value--loss {
  color: var(--color-error, #e74c3c);
}

.stat-value--profit {
  color: var(--color-success, #2ecc71);
}

.resources-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.resources-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary, #b1bad3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 8px;
}

.resources-links {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.resource-link {
  font-size: 0.8125rem;
  color: var(--color-primary, #1475e1);
  text-decoration: none;
  transition: opacity var(--transition-fast, 150ms ease);
}

.resource-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.resource-link--internal {
  display: inline-block;
  font-size: 0.75rem;
  color: var(--text-secondary, #b1bad3);
}

.btn {
  padding: 10px 20px;
  border-radius: var(--radius-md, 8px);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  border: none;
}

.btn-primary {
  background: var(--color-primary, #1475e1);
  color: var(--color-on-dark, #fff);
}

.btn-primary:hover {
  opacity: 0.9;
}

.btn-outline {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid var(--border-color, rgba(255, 255, 255, 0.1));
}

.btn-outline:hover {
  color: var(--text-primary, #fff);
  border-color: var(--text-secondary, #b1bad3);
}
</style>
